import { useState, useRef, useCallback } from 'react';
import ReactCrop, { type Crop, type PixelCrop, centerCrop, makeAspectCrop } from 'react-image-crop';
import 'react-image-crop/dist/ReactCrop.css';

interface ImageCropModalProps {
  open: boolean;
  imageSrc: string;
  aspectRatio?: number;
  title?: string;
  description?: string;
  onConfirm: (croppedBlob: Blob) => void;
  onCancel: () => void;
}

function centerAspectCrop(mediaWidth: number, mediaHeight: number, aspect: number): Crop {
  return centerCrop(makeAspectCrop({ unit: '%', width: 80 }, aspect, mediaWidth, mediaHeight), mediaWidth, mediaHeight);
}

// Target height for sidebar logo: 72px (2× retina for h-9 = 36px)
const TARGET_LOGO_HEIGHT = 72;
// Target size for favicon
const TARGET_FAVICON_SIZE = 48;

function getCroppedCanvas(
  image: HTMLImageElement,
  crop: PixelCrop,
  outputType: 'logo' | 'favicon' = 'logo',
): HTMLCanvasElement {
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d')!;
  const scaleX = image.naturalWidth / image.width;
  const scaleY = image.naturalHeight / image.height;

  // Cropped region in natural pixels
  const cropW = crop.width * scaleX;
  const cropH = crop.height * scaleY;

  if (outputType === 'favicon') {
    // Favicon: always output as TARGET_FAVICON_SIZE × TARGET_FAVICON_SIZE
    canvas.width = TARGET_FAVICON_SIZE;
    canvas.height = TARGET_FAVICON_SIZE;
  } else {
    // Logo: scale to TARGET_LOGO_HEIGHT, width proportional
    // If cropped image is already large enough, keep original size (up to reasonable max)
    const ratio = cropW / cropH;
    const outHeight = Math.max(TARGET_LOGO_HEIGHT, Math.min(cropH, 278));
    const outWidth = Math.round(outHeight * ratio);
    canvas.width = outWidth;
    canvas.height = outHeight;
  }

  // Use high-quality scaling
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = 'high';

  ctx.drawImage(image, crop.x * scaleX, crop.y * scaleY, cropW, cropH, 0, 0, canvas.width, canvas.height);

  return canvas;
}

const ASPECT_OPTIONS = [
  { label: 'Free', value: 0 },
  { label: '5:1 (Horizontal)', value: 5 / 1 },
  { label: '3:1 (Wide)', value: 3 / 1 },
  { label: '16:9', value: 16 / 9 },
  { label: '1:1 (Square)', value: 1 },
];

const FAVICON_ASPECT_OPTIONS = [{ label: '1:1 (Square)', value: 1 }];

export function ImageCropModal({
  open,
  imageSrc,
  aspectRatio,
  title = 'Crop Image',
  description,
  onConfirm,
  onCancel,
}: ImageCropModalProps) {
  const imgRef = useRef<HTMLImageElement>(null);
  const [crop, setCrop] = useState<Crop>();
  const [completedCrop, setCompletedCrop] = useState<PixelCrop>();
  const [selectedAspect, setSelectedAspect] = useState<number>(aspectRatio ?? 0);
  const isFavicon = aspectRatio === 1;
  const aspectOptions = isFavicon ? FAVICON_ASPECT_OPTIONS : ASPECT_OPTIONS;

  const onImageLoad = useCallback(
    (e: React.SyntheticEvent<HTMLImageElement>) => {
      const { width, height } = e.currentTarget;
      const aspect = selectedAspect || undefined;
      if (aspect) {
        setCrop(centerAspectCrop(width, height, aspect));
      } else {
        setCrop({ unit: '%', x: 10, y: 10, width: 80, height: 80 });
      }
    },
    [selectedAspect],
  );

  const handleAspectChange = (aspect: number) => {
    setSelectedAspect(aspect);
    if (imgRef.current) {
      const { width, height } = imgRef.current;
      if (aspect) {
        setCrop(centerAspectCrop(width, height, aspect));
      }
    }
  };

  const handleConfirm = () => {
    if (!imgRef.current || !completedCrop) return;
    const outputType = isFavicon ? 'favicon' : 'logo';
    const canvas = getCroppedCanvas(imgRef.current, completedCrop, outputType);
    canvas.toBlob(
      (blob) => {
        if (blob) onConfirm(blob);
      },
      'image/png',
      1,
    );
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="mx-4 flex max-h-[90vh] w-full max-w-2xl flex-col rounded-2xl bg-white shadow-xl">
        {/* Header */}
        <div className="border-b border-border px-6 py-4">
          <h3 className="text-lg font-semibold text-foreground">{title}</h3>
          {description && <p className="mt-1 text-sm text-muted-foreground">{description}</p>}
        </div>

        {/* Aspect Ratio Selector */}
        <div className="flex gap-2 border-b border-border px-6 py-3">
          <span className="mr-1 text-xs font-medium text-muted-foreground self-center">Aspect:</span>
          {aspectOptions.map((opt) => (
            <button
              key={opt.label}
              type="button"
              onClick={() => handleAspectChange(opt.value)}
              className={`rounded-full px-3 py-1 text-xs font-medium transition-colors ${
                selectedAspect === opt.value
                  ? 'bg-primary text-white'
                  : 'bg-muted text-muted-foreground hover:bg-muted/80'
              }`}
            >
              {opt.label}
            </button>
          ))}
        </div>

        {/* Crop Area */}
        <div className="flex-1 overflow-auto px-6 py-4">
          <div className="flex items-center justify-center">
            <ReactCrop
              crop={crop}
              onChange={(c) => setCrop(c)}
              onComplete={(c) => setCompletedCrop(c)}
              aspect={selectedAspect || undefined}
              minWidth={20}
              minHeight={20}
            >
              <img
                ref={imgRef}
                src={imageSrc}
                alt="Crop preview"
                onLoad={onImageLoad}
                className="max-h-[50vh] max-w-full"
                crossOrigin="anonymous"
              />
            </ReactCrop>
          </div>
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-3 border-t border-border px-6 py-4">
          <button
            type="button"
            onClick={onCancel}
            className="rounded-lg border border-input bg-white px-4 py-2 text-sm font-medium text-foreground hover:bg-muted"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleConfirm}
            disabled={!completedCrop}
            className="rounded-lg bg-primary px-4 py-2 text-sm font-medium text-white hover:bg-primary/90 disabled:opacity-50"
          >
            Crop & Upload
          </button>
        </div>
      </div>
    </div>
  );
}
